/**
 * Canvas de assinatura manuscrita.
 *
 * Feito à mão em vez de uma dependência porque o traço precisa sair bom no PDF
 * final: o canvas é renderizado na resolução do dispositivo (devicePixelRatio)
 * e o PNG é recortado na área efetivamente desenhada, sem a moldura vazia.
 *
 * A suavização usa curvas quadráticas entre pontos médios — sem isso o traço no
 * celular sai poligonal, com cantos visíveis a cada evento de toque.
 */

import { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/BackspaceOutlined';

export interface SignaturePadHandle {
  /** PNG recortado no traço, ou null quando o canvas está em branco. */
  toDataUrl: () => string | null;
  clear: () => void;
  isEmpty: () => boolean;
}

interface Point {
  x: number;
  y: number;
}

interface SignaturePadProps {
  height?: number;
  strokeColor?: string;
  onChange?: (hasContent: boolean) => void;
  disabled?: boolean;
}

/** Largura do traço em pixels CSS. Fino demais some no PDF; grosso demais borra. */
const STROKE_WIDTH = 2.6;

const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(function SignaturePad(
  { height = 220, strokeColor = '#0f172a', onChange, disabled = false },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<Point | null>(null);
  const hasContentRef = useRef(false);
  /** Retângulo do que foi desenhado, em pixels do canvas, para recortar no fim. */
  const boundsRef = useRef<{ minX: number; minY: number; maxX: number; maxY: number } | null>(null);

  const [hasContent, setHasContent] = useState(false);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0) return;

    canvas.width = Math.floor(rect.width * ratio);
    canvas.height = Math.floor(rect.height * ratio);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = STROKE_WIDTH;
    ctx.strokeStyle = strokeColor;
  }, [strokeColor]);

  // Girar o celular redimensiona o canvas e zera o bitmap. Redesenhar o traço
  // exigiria guardar todos os pontos; avisar e limpar é mais honesto do que
  // deixar uma assinatura pela metade ser enviada.
  useEffect(() => {
    setupCanvas();

    const handleResize = () => {
      setupCanvas();
      hasContentRef.current = false;
      boundsRef.current = null;
      setHasContent(false);
      onChange?.(false);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [setupCanvas, onChange]);

  const pointFromEvent = (event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const trackBounds = (point: Point) => {
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const x = point.x * ratio;
    const y = point.y * ratio;
    const pad = STROKE_WIDTH * ratio;

    const current = boundsRef.current;
    boundsRef.current = current
      ? {
          minX: Math.min(current.minX, x - pad),
          minY: Math.min(current.minY, y - pad),
          maxX: Math.max(current.maxX, x + pad),
          maxY: Math.max(current.maxY, y + pad),
        }
      : { minX: x - pad, minY: y - pad, maxX: x + pad, maxY: y + pad };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.setPointerCapture(event.pointerId);
    drawingRef.current = true;

    const point = pointFromEvent(event);
    lastPointRef.current = point;
    trackBounds(point);

    // Um toque único precisa deixar marca: sem isso, pingar o "i" não desenha nada.
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.arc(point.x, point.y, STROKE_WIDTH / 2, 0, Math.PI * 2);
      ctx.fillStyle = strokeColor;
      ctx.fill();
    }

    if (!hasContentRef.current) {
      hasContentRef.current = true;
      setHasContent(true);
      onChange?.(true);
    }
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || disabled) return;
    event.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const last = lastPointRef.current;
    if (!canvas || !ctx || !last) return;

    const point = pointFromEvent(event);
    const mid = { x: (last.x + point.x) / 2, y: (last.y + point.y) / 2 };

    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.quadraticCurveTo(last.x, last.y, mid.x, mid.y);
    ctx.stroke();

    lastPointRef.current = point;
    trackBounds(point);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    try {
      canvasRef.current?.releasePointerCapture(event.pointerId);
    } catch {
      /* ponteiro já liberado */
    }
  };

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    ctx.clearRect(0, 0, canvas.width / ratio, canvas.height / ratio);

    hasContentRef.current = false;
    boundsRef.current = null;
    setHasContent(false);
    onChange?.(false);
  }, [onChange]);

  useImperativeHandle(
    ref,
    () => ({
      isEmpty: () => !hasContentRef.current,
      clear,
      toDataUrl: () => {
        const canvas = canvasRef.current;
        const bounds = boundsRef.current;
        if (!canvas || !hasContentRef.current || !bounds) return null;

        const minX = Math.max(Math.floor(bounds.minX), 0);
        const minY = Math.max(Math.floor(bounds.minY), 0);
        const width = Math.min(Math.ceil(bounds.maxX), canvas.width) - minX;
        const height = Math.min(Math.ceil(bounds.maxY), canvas.height) - minY;
        if (width <= 0 || height <= 0) return null;

        // Recorte com fundo branco: PNG transparente fica invisível sobre o
        // fundo escuro de alguns leitores de PDF.
        const out = document.createElement('canvas');
        out.width = width;
        out.height = height;
        const outCtx = out.getContext('2d');
        if (!outCtx) return null;
        outCtx.fillStyle = '#ffffff';
        outCtx.fillRect(0, 0, width, height);
        outCtx.drawImage(canvas, minX, minY, width, height, 0, 0, width, height);

        return out.toDataURL('image/png');
      },
    }),
    [clear],
  );

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.75 }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Desenhe sua assinatura
        </Typography>
        <Button
          size="small"
          startIcon={<ClearIcon fontSize="small" />}
          onClick={clear}
          disabled={disabled || !hasContent}
          sx={{ textTransform: 'none', minWidth: 0 }}
        >
          Limpar
        </Button>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          borderRadius: 2,
          bgcolor: '#F1F5F9',
          border: '1px solid',
          borderColor: hasContent ? 'primary.main' : 'divider',
          transition: 'border-color 160ms ease',
          overflow: 'hidden',
        }}
      >
        {/* Linha-guia: dá ao signatário a referência de onde assinar. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 24,
            right: 24,
            bottom: '30%',
            borderBottom: '1px dashed',
            borderColor: 'rgba(100,116,139,0.35)',
            pointerEvents: 'none',
          }}
        />
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{
            display: 'block',
            width: '100%',
            height,
            // Sem isso o gesto de desenhar rola a página no celular.
            touchAction: 'none',
            cursor: disabled ? 'not-allowed' : 'crosshair',
          }}
        />
      </Box>
    </Box>
  );
});

export default SignaturePad;
